import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { companyInfo, contactServices } from '@/mocks/home';
import { getGenericWhatsAppUrl } from '@/utils/whatsapp';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const { ref: contentRef, isVisible } = useScrollAnimation(0.05);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const name = formData.get('name')?.toString().trim() || '';
    const phone = formData.get('phone')?.toString().trim() || '';
    const email = formData.get('email')?.toString().trim() || '';
    const service = formData.get('service')?.toString().trim() || '';
    const message = formData.get('message')?.toString().trim() || '';

    // Validate all required fields
    const missingFields: string[] = [];
    if (!name) missingFields.push('Name');
    if (!phone) missingFields.push('Phone');
    if (!email) missingFields.push('Email');
    if (!service) missingFields.push('Service Required');
    if (!message) missingFields.push('Message');

    if (missingFields.length > 0) {
      setFormStatus('error');
      setFormError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    // Build dynamic WhatsApp message from form values
    const whatsappMessage = `Hello Infinite Consultant & Services,

I would like to enquire about your services.

-------------------------
Name:
${name}

Phone:
${phone}

Email:
${email}

Service Required:
${service}

Project Requirement:
${message}
-------------------------

Please contact me.`;

    window.open(`https://wa.me/917019908718?text=${encodeURIComponent(whatsappMessage)}`, '_blank');

    setFormStatus('success');
    form.reset();
  };

  return (
    <Layout>
      <JsonLd data={getWebPageSchema('Contact Infinite Consultant & Services | Get Free Quotation in Bangalore', 'Contact Infinite Consultant & Services in Bangalore for free consultation and quotation on landscaping interiors construction cleaning and property maintenance. Call or WhatsApp now.', 'https://infiniteconsultant.com/contact')} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }, { name: 'Contact', url: 'https://infiniteconsultant.com/contact' }])} />
      {/* Hero */}
      <section className="relative h-[350px] md:h-[450px] w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20Indian%20professional%20office%20reception%20area%20Bangalore%20with%20warm%20welcoming%20atmosphere%20contact%20desk%20comfortable%20seating%20natural%20light%20indoor%20plants%20Indian%20corporate%20hospitality%20professional%20photography&width=1600&height=800&seq=contact-hero&orientation=landscape&nocache=true"
          alt="Contact Us"
          className="absolute inset-0 w-full h-full object-cover object-top animate-reveal"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 sm:px-6 lg:px-10 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
                <span className="text-white/50">/</span>
                <span className="text-white whitespace-nowrap">Contact</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-3" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards' }}>
                Get In Touch
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards' }}>
                Ready to transform your space? Reach out for a free consultation and quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div ref={contentRef} className={`max-w-6xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
                  <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-5">Contact Information</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                        <i className="ri-phone-line text-primary-500" />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">Phone</div>
                        <a href={`tel:${companyInfo.phone1.replace(/\s/g, '')}`} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors cursor-pointer">
                          {companyInfo.phone1}
                        </a>
                        <div>
                          <a href={`tel:${companyInfo.phone2.replace(/\s/g, '')}`} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors cursor-pointer">
                            {companyInfo.phone2}
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-secondary-50 flex items-center justify-center shrink-0">
                        <i className="ri-whatsapp-line text-secondary-500" />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">WhatsApp</div>
                        <a
                          href={getGenericWhatsAppUrl()}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-sm font-medium text-foreground-800 hover:text-secondary-500 transition-colors cursor-pointer"
                        >
                          {companyInfo.phone1}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
                        <i className="ri-mail-line text-accent-500" />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">Email</div>
                        <a href={`mailto:${companyInfo.email}`} className="text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors break-all cursor-pointer">
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                        <i className="ri-map-pin-line text-primary-500" />
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">Office Address</div>
                        <div className="text-sm font-medium text-foreground-800">{companyInfo.address}</div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-background-200/70">
                      <div className="text-xs text-foreground-500 mb-3">Business Hours</div>
                      <div className="space-y-2 text-sm text-foreground-700">
                        <div className="flex justify-between"><span>Monday - Saturday</span><span className="font-medium">9:00 AM — 7:00 PM</span></div>
                        <div className="flex justify-between"><span>Sunday</span><span className="font-medium text-foreground-500">Closed</span></div>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-background-200/70">
                      <div className="text-xs text-foreground-500 mb-2">Follow Us</div>
                      <div className="flex items-center gap-3">
                        <a
                          href={companyInfo.facebook}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          <div className="w-4 h-4 flex items-center justify-center text-secondary-500">
                            <i className="ri-facebook-fill" />
                          </div>
                          Facebook Page
                        </a>
                        <a
                          href="https://www.instagram.com/infiniteconsultantandservices"
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          <div className="w-4 h-4 flex items-center justify-center text-accent-500">
                            <i className="ri-instagram-line" />
                          </div>
                          Instagram
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl p-5 md:p-6 border border-background-200/70">
                  <h2 className="font-heading font-semibold text-lg text-foreground-950 mb-5">Send Us a Message</h2>

                  <form data-readdy-form id="contact-form-d9blq6ec87cp3jcuj2t0" onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">Name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-300"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">Phone</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-300"
                          placeholder="Your phone number"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-300"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-foreground-700 mb-1.5">Service Required</label>
                      <select
                        id="service"
                        name="service"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-300 appearance-none"
                      >
                        <option value="">Select a service</option>
                        {contactServices.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-300 resize-none"
                        placeholder="Tell us about your project requirements..."
                      />
                      <div className="text-xs text-foreground-400 mt-1 text-right">Max 500 characters</div>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field"
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="btn-ripple w-full sm:w-auto px-8 py-3 rounded-lg bg-gradient-brand text-white font-medium text-sm hover:bg-gradient-brand-hover transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        'Send Message'
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary-50 border border-secondary-200/70 text-secondary-700 text-sm animate-fade-in">
                        <i className="ri-check-line text-lg" />
                        Thank you! We will get back to you soon.
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200/70 text-red-700 text-sm animate-fade-in">
                        <i className="ri-error-warning-line text-lg" />
                        {formError || 'Something went wrong. Please try again.'}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            <div className="mt-10 md:mt-12">
              <div className="bg-white rounded-xl overflow-hidden border border-background-200/70">
                <iframe
                  title="Infinite Consultant & Services Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.553663740883!2d77.57746787411979!3d12.936383515651315!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae15cc82660043%3A0xbaa1f27fa80223ac!2sINFINITE%20CONSULTANT%20AND%20SERVICES!5e0!3m2!1sen!2sin!4v1784111267305!5m2!1sen!2sin"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}